/*
 * @providesModule NetClient
 *
 */

// @ts-ignore sdk-mobile dependency
import APIUtilities from 'APIUtilities';
// @ts-ignore sdk-mobile dependency
import I18n from 'INTL';
// @ts-ignore sdk-mobile dependency
import {ErrorResponse} from 'NetClientTypes';

import {handleError as handleGeneralError} from '@/services/handleError';
// @ts-ignore sdk-mobile dependency
import LocationPermissionController from 'LocationPermissionController';
import {getCurrentPosition, GEOLOCATION_KEY} from '@/utils/location';
import {addBrowserSideAppHeaders, rerouteShellUrl} from '@/utils/unityHeaders';
import {RequestCoalescer} from './RequestCoalescer';

const NetClient = {
  lastPositionFetched: null as GeolocationPosition | null,
  isRefreshingLocation: false,

  // Shared request coalescing utility
  _requestCoalescer: new RequestCoalescer(),

  handleError(
    error: ErrorResponse,
    callback?: (error: ErrorResponse, payload: unknown | null) => unknown
  ) {
    console.debug('NetClient: handle API error', error);
    if (callback) {
      callback(error, null);
    }
  },

  async fetchTransactionURL(
    method: string,
    url: string,
    params: Record<string, string>,
    isTransaction: boolean,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ): Promise<string | undefined> {
    if (APIUtilities.checkValidURL(url)) {
      try {
        let json = '{}';
        const fullURL = APIUtilities.generateURL(url, isTransaction);

        const transactionId = await APIUtilities.createUUID();
        if (params) {
          params.transaction_id = transactionId;
          json = JSON.stringify(params);
        } else {
          json = JSON.stringify({transaction_id: transactionId});
        }
        const headers = await this.getDefaultHeaders({'X-Skillz-Transaction-Id': transactionId});

        const response = await fetch(rerouteShellUrl(fullURL),{method: method, body: json, headers: headers});
        let payload = {};

        if (response.status != 204) {
          payload = await response.json();
        }
        if (!response.ok) {
          // Formatting so that it matches iOS
          console.debug(
            'NetClient: fetchTransactionURL: response bad: ' + JSON.stringify(response)
          );
          this.handleError({responseObject: payload, code: response.status}, callback);
          return;
        }
        console.debug('NetClient: fetchTransactionURL: response ok');
        if (callback) {
          try {
            callback(null, payload);
          } catch (e) {
            console.warn('NetClient: fetchTransactionURL: callback exception: ' + e);
          }
        }
      } catch (error) {
        this.handleError(error, callback);
      }
    } else {
      console.debug('NetClient: fetchTransactionURL: invalid url: ' + url);
      this.handleError(
        {
          responseObject: [
            {
              name: 'MALFORMED_REQUST',
              message: I18n.message("Uh oh! Doesn't look like that worked. Please try again."),
            },
          ],
        },
        callback
      );
    }
  },

  // Mirrors this.fetchTransactionURL() for GET Requests as React Native
  // doesn't allow a body for GET requests
  async getTransactionURLRequest(
    method: string,
    url: string,
    params: Record<string, string>,
    isTransaction: boolean,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ): Promise<string | undefined> {
    if (method !== 'GET') {
      this.fetchTransactionURL(method, url, params, isTransaction, retryMode, callback);
      return;
    }

    if (APIUtilities.checkValidURL(url)) {
      try {
        const fullURL =
          APIUtilities.generateURL(url, isTransaction) + this.createQueryParams(params);

        const transactionId = await APIUtilities.createUUID();

        const headers = await this.getDefaultHeaders({'X-Skillz-Transaction-Id': transactionId});

        const response = await fetch(rerouteShellUrl(fullURL),{method: method, body: null, headers: headers});
        let payload = {};

        if (response.status != 204) {
          payload = await response.json();
        }
        if (!response.ok) {
          handleGeneralError(response, url, {method, headers, body: null});
          // Formatting so that it matches iOS
          console.debug(
            'NetClient: getTransactionURLRequest: response bad: ' + JSON.stringify(response)
          );
          this.handleError({responseObject: payload, code: response.status}, callback);
          return;
        }
        console.debug('NetClient: getTransactionURLRequest: response ok');
        if (callback) {
          try {
            callback(null, payload);
          } catch (e) {
            console.warn('NetClient: getTransactionURLRequest: callback exception: ' + e);
          }
        }
      } catch (error) {
        this.handleError(error, callback);
      }
    } else {
      console.debug('NetClient: getTransactionURLRequest: invalid url: ' + url);
      this.handleError(
        {
          responseObject: [
            {
              name: 'MALFORMED_REQUST',
              message: I18n.message("Uh oh! Doesn't look like that worked. Please try again."),
            },
          ],
        },
        callback
      );
    }
  },

  createQueryParams(params: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
      return '';
    }

    // Filter out null and undefined values to match original behavior
    const filteredParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        filteredParams[key] = String(value);
      }
    }

    if (Object.keys(filteredParams).length === 0) {
      return '';
    }

    const urlParams = new URLSearchParams(filteredParams);
    const queryString = urlParams.toString();
    return queryString ? '?' + queryString : '';
  },

  async fetchURLAsync(
    method: string,
    url: string,
    params: Record<string, string>,
    headers: Record<string, string>,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ) {
    if (APIUtilities.checkValidURL(url)) {
      try {
        let json = '{}';
        let actualHeaders = await this.getDefaultHeaders({});
        const fullURL = APIUtilities.generateURL(url);

        if (params) {
          json = JSON.stringify(params);

          if (params.transaction_id) {
            actualHeaders = {
              ...actualHeaders,
              // @ts-ignore
              'X-Skillz-Transaction-Id': params.transaction_id,
            };
          }
        }

        actualHeaders = {
          ...headers,
          ...actualHeaders,
        };

        const response = await fetch(rerouteShellUrl(fullURL),{
          method: method,
          body: json,
          headers: actualHeaders,
        });
        let payload = {};
        const contentLength = response.headers.get('content-length');
        if (response.status != 204 && contentLength != '0' && response.status !== 202) {
          payload = await response.json();
        }
        if (!response.ok) {
          handleGeneralError(response, url, {method, headers, body: null});

          // Formatting so that it matches iOS
          this.handleError({responseObject: payload, code: response.status}, callback);
          return;
        }

        if (callback) {
          callback(null, payload);
        }
      } catch (error) {
        this.handleError(error, callback);
      }
    } else {
      this.handleError(
        {
          responseObject: [
            {
              name: 'MALFORMED_REQUST',
              message: I18n.message("Uh oh! Doesn't look like that worked. Please try again."),
            },
          ],
        },
        callback
      );
    }
  },

  fetchURL(
    method: string,
    url: string,
    params: Record<string, string>,
    headers: Record<string, string>
  ): Promise<unknown> | undefined {
    if (APIUtilities.checkValidURL(url)) {
      const fullURL = APIUtilities.generateURL(url);

      // @ts-ignore
      console.debug('NetClient: full fetchURL: ' + fullURL);

      let json = '{}';
      if (params) {
        json = JSON.stringify(params);
      }

      return this.getDefaultHeaders({})
        .then((defaultHeaders) => {
          const actualHeaders = {
            ...defaultHeaders,
            ...headers,
          };

          if (params?.transaction_id) {
            actualHeaders['X-Skillz-Transaction-Id'] = params.transaction_id;
          }
          console.log(actualHeaders);
          return fetch(rerouteShellUrl(fullURL),{
            method: method,
            body: json,
            headers: actualHeaders,
          });
        })
        .then((response: Response) => {
          if (response.status === 204) {
            return {};
          }

          if (response.ok) {
            return response.json();
          } else {
            handleGeneralError(response, url, {
              method: method,
              body: json,
              headers: headers,
            });

            return response.json().then((responseJson) => {
              throw responseJson;
            });
          }
        })
        .catch((error) => {
          console.error('netclient fetchURL error:', error);
          throw error;
        });
    } else {
      return undefined;
    }
  },

  postURL(
    url: string,
    params: Record<string, string>,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ): unknown | undefined {
    return this.postURLWithHeaders(url, params, {}, retryMode, callback);
  },

  patchURL(
    url: string,
    params: Record<string, string>,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ) {
    return this.patchURLWithHeaders(url, params, {}, retryMode, callback);
  },

  putURL(
    url: string,
    params: Record<string, string>,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ) {
    return this.putURLWithHeaders(url, params, {}, retryMode, callback);
  },

  deleteURL(url: string, params: Record<string, string>, retryMode: string, callback: () => null) {
    return this.deleteURLWithHeaders(url, params, {}, retryMode, callback);
  },

  getURL(url: string, params: Record<string, string>): Promise<unknown> {
    return this.getURLWithHeaders(url, params, {});
  },

  postURLWithHeaders(
    url: string,
    params: Record<string, string>,
    headers: Record<string, string>,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ): unknown | null {
    if (callback) {
      this.fetchURLAsync('POST', url, params, headers, retryMode, callback);
      return null;
    }
    return this.fetchURL('POST', url, params, headers);
  },

  patchURLWithHeaders(
    url: string,
    params: Record<string, string>,
    headers: Record<string, string>,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ) {
    if (callback) {
      this.fetchURLAsync('PATCH', url, params, headers, retryMode, callback);
      return null;
    }
    return this.fetchURL('PATCH', url, params, headers);
  },

  putURLWithHeaders(
    url: string,
    params: Record<string, string>,
    headers: Record<string, string>,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ) {
    if (callback) {
      this.fetchURLAsync('PUT', url, params, headers, retryMode, callback);
      return null;
    }
    return this.fetchURL('PUT', url, params, headers);
  },

  deleteURLWithHeaders(
    url: string,
    params: Record<string, string>,
    headers: Record<string, string>,
    retryMode: string,
    callback: (error: unknown, payload: unknown) => void
  ): unknown | null {
    if (callback) {
      this.fetchURLAsync('DELETE', url, params, headers, retryMode, callback);
      return null;
    }
    return this.fetchURL('DELETE', url, params, headers);
  },

  getURLWithHeaders(
    url: string,
    params: Record<string, string>,
    headers: Record<string, string>
  ): Promise<unknown | undefined> {
    if (!APIUtilities.checkValidURL(url)) {
      return Promise.resolve(undefined);
    }

    // Check if we have a coalesced request for this URL and params
    const coalescedRequest = this._requestCoalescer.checkCoalescedRequest(url, params);
    if (coalescedRequest) {
      // @ts-ignore
      console.debug('RequestCoalescer: returning coalesced request for URL: ' + url);
      return coalescedRequest;
    }

    // Create new request
    let fullURL = APIUtilities.generateURL(url);

    if (params != null) {
      const urlParams = new URLSearchParams(params);
      const queryString = urlParams.toString();
      if (queryString) {
        fullURL += '?' + queryString;
      }
    }

    // @ts-ignore
    console.debug('NetClient: full getURL: ' + fullURL);

    const requestPromise = this.getDefaultHeaders({})
      .then((defaultHeaders) => {
        const actualHeaders = {
          ...defaultHeaders,
          ...headers,
        };

        return fetch(rerouteShellUrl(fullURL),{headers: actualHeaders});
      })
      .then(async (response) => {
        if (response.ok) {
          return response.text().then((responseText) => {
            const contentLength = response.headers.get('content-length');
            if (response.status !== 204 && contentLength !== '0') {
              return JSON.parse(responseText);
            } else {
              return null;
            }
          });
        } else {
          if (response.headers.get('content-type')?.includes('application/json')) {
            const responseJson = await response.json();
            // @ts-ignore
            response.responseObject = responseJson;
          }
          // @ts-ignore
          response.code = response.status;
          return Promise.reject(response);
        }
      })
      .catch((error) => {
        // Background fetches fired without a game/unity context (the anonymous
        // partner catalogue triggers sdk-core's BadgeController LTO fetch, which
        // is failSilently and has no caller that handles the rejection). They
        // can never return data, so resolve to null rather than surfacing an
        // unhandled promise rejection. Genuine errors still reject.
        const message = error instanceof Error ? error.message : String(error);
        if (/no game context|unity config not set/i.test(message)) {
          return null;
        }
        return Promise.reject(error);
      });

    // Cache the promise using the shared coalescer
    this._requestCoalescer.cacheRequest(url, params, requestPromise);

    return requestPromise;
  },

  async getDefaultHeaders(additionalHeaders): Promise<Record<string, string>> {
    const headers = additionalHeaders || {};
    await addBrowserSideAppHeaders(headers);

    headers['Content-Type'] = 'application/json';

    try {
      //@ts-ignore
      const ua = await navigator?.userAgentData?.getHighEntropyValues([
        'architecture',
        'bitness',
        'fullVersionList',
        'model',
        'platform',
        'platformVersion',
        'uaFullVersion',
      ]);
      headers['X-Skillz-Device-Entropy'] = `${ua?.model}`;
    } catch (e) {
      console.error('Error getting high entropy values:', e);
    }

    if (LocationPermissionController.isLocationEnabled) {
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000; // Only fetch a new location every five minutes
      const needsRefresh =
        !this.lastPositionFetched || now - this.lastPositionFetched.timestamp >= fiveMinutes;

      // Check if we need to refresh location data in the background
      if (needsRefresh && !this.isRefreshingLocation) {
        // Set flag to prevent multiple simultaneous requests
        this.isRefreshingLocation = true;

        getCurrentPosition()
          .then((position: GeolocationPosition) => {
            //console.log('location fetch took ' + (Date.now() - now) + 'ms');
            //todo add DD tracking for how long it takes to fetch a location
            //@ts-ignore // I don't know why this throws a type error.
            this.lastPositionFetched = position;
            this.isRefreshingLocation = false;
          })
          .catch(() => {
            this.isRefreshingLocation = false;
          });
      }

      // Add location from cache if available
      if (this.lastPositionFetched) {
        const {coords, timestamp} = this.lastPositionFetched;
        if (coords && timestamp) {
          headers['X-Skillz-Location'] = `${coords.latitude},${coords.longitude},${timestamp}`;
        }
      }
    } else {
      const storedPosition = localStorage.getItem(GEOLOCATION_KEY);
      if (storedPosition) {
        const position = JSON.parse(atob(storedPosition));
        if (position?.coords && position?.timestamp) {
          const locationFormatHeader =
            position.coords.latitude + ',' + position.coords.longitude + ',' + position.timestamp;
          headers['X-Skillz-Location'] = locationFormatHeader;
        }
      }
    }

    return headers;
  },
};

export {NetClient as default};
